<!--
  LoginForm.vue
  Núcleo reutilizable del formulario de inicio de sesión.
  Puede embeberse en una página completa (mode="page") o dentro de un dialog (mode="dialog").
  En mode="page" ejecuta el ruteo post-login según role, userState y query params.
  En mode="dialog" emite "logged-in" y deja al consumidor decidir.
-->
<template>
  <div :class="mode === 'page' ? 'loginform-page' : 'loginform-dialog'">
    <div v-if="mode === 'page'" class="loginform-page__decor loginform-page__decor--orb-one" aria-hidden="true"></div>
    <div v-if="mode === 'page'" class="loginform-page__decor loginform-page__decor--orb-two" aria-hidden="true"></div>
    <div v-if="mode === 'page'" class="loginform-page__dots loginform-page__dots--top" aria-hidden="true"></div>
    <div v-if="mode === 'page'" class="loginform-page__dots loginform-page__dots--bottom" aria-hidden="true"></div>

    <router-link v-if="mode === 'page'" to="/" class="loginform-back-button">
      <v-icon small>mdi-arrow-left</v-icon>
      <span>Regresar</span>
    </router-link>

    <div :class="mode === 'page' ? 'loginform-page__layout' : 'loginform-dialog__layout'">
      <section
        :class="mode === 'page' ? 'loginform-page__card' : 'loginform-dialog__card'"
        aria-labelledby="loginform-card-title"
      >
        <div class="loginform-card__header">
          <span
            class="loginform-mark"
            :class="{
              'loginform-mark--association': associationProfilePicture,
              'loginform-mark--default': !associationProfilePicture,
            }"
            aria-label="WeChamber"
          >
            <transition name="loginform-mark-flip" mode="out-in">
              <img
                :key="associationProfilePicture || defaultProfilePicture"
                :src="associationProfilePicture || defaultProfilePicture"
                alt=""
                @error="handleAssociationImageError"
              />
            </transition>
          </span>
          <h2 id="loginform-card-title">¡Hola!</h2>
          <p>Inicia sesión para continuar</p>
        </div>

        <form class="loginform-form" @submit.prevent="_login">
          <div class="loginform-field">
            <label for="loginform-email">Correo electrónico</label>
            <div
              class="loginform-field__control"
              :class="{ 'loginform-field__control--error': emailError || emailLookupError }"
            >
              <v-icon aria-hidden="true">mdi-email-outline</v-icon>
              <input
                id="loginform-email"
                v-model.trim="dataUser.email"
                type="email"
                autocomplete="email"
                placeholder="ejemplo@correo.com"
                @blur="validateField('email')"
                @input="handleEmailInput"
                @keyup.enter="_login"
              />
            </div>
            <span v-if="emailError" class="loginform-field__error">{{ emailError }}</span>
            <span v-else-if="emailLookupError" class="loginform-field__error">{{ emailLookupError }}</span>
          </div>

          <div class="loginform-field">
            <label for="loginform-password">Contraseña</label>
            <div
              class="loginform-field__control"
              :class="{ 'loginform-field__control--error': passwordError }"
            >
              <v-icon aria-hidden="true">mdi-lock-outline</v-icon>
              <input
                id="loginform-password"
                v-model="dataUser.password"
                :type="showPass ? 'text' : 'password'"
                autocomplete="current-password"
                placeholder="Ingresa tu contraseña"
                @blur="validateField('password')"
                @input="passwordError = ''"
                @keyup.enter="_login"
              />
              <button
                type="button"
                class="loginform-field__toggle"
                :aria-label="showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                @click="showPass = !showPass"
              >
                <v-icon aria-hidden="true">
                  {{ showPass ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
                </v-icon>
              </button>
            </div>
            <span v-if="passwordError" class="loginform-field__error">{{ passwordError }}</span>
          </div>

          <div class="loginform-options">
            <label class="loginform-remember">
              <input v-model="rememberMe" type="checkbox" />
              <span class="loginform-remember__box" aria-hidden="true"></span>
              <span>Recuérdame</span>
            </label>
            <button v-if="mode === 'page'" type="button" class="loginform-forgot" @click="passwordRecover">
              ¿Has olvidado tu contraseña?
            </button>
            <button v-else type="button" class="loginform-forgot" @click="$emit('recover-password')">
              ¿Has olvidado tu contraseña?
            </button>
          </div>

          <button
            type="submit"
            class="loginform-submit"
            :disabled="isLoginDisabled"
          >
            <span>Iniciar sesión</span>
            <v-progress-circular
              v-if="loadingButton"
              indeterminate
              color="#1b273e"
              size="18"
              width="2"
            />
            <v-icon v-else aria-hidden="true">mdi-arrow-right</v-icon>
          </button>

          <div class="loginform-divider" aria-hidden="true">
            <span></span>
            <small>o continúa con</small>
            <span></span>
          </div>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span class="loginform-google-wrapper" v-bind="attrs" v-on="on">
                <button
                  type="button"
                  class="loginform-google"
                  disabled
                  aria-disabled="true"
                  aria-label="Continuar con Google. Próximamente"
                >
                  <img
                    class="loginform-google__mark"
                    src="@/assets/images/weChamber/google.png"
                    alt=""
                    aria-hidden="true"
                  />
                  <span>Continuar con Google</span>
                </button>
              </span>
            </template>
            <span>Próximamente</span>
          </v-tooltip>
        </form>
      </section>

      <p v-if="mode === 'page'" class="loginform-terms">
        Al iniciar sesión, aceptas nuestros
        <router-link to="/terminos-y-condiciones">Términos y condiciones</router-link>
        y
        <router-link to="/aviso-de-privacidad">Política de privacidad</router-link>
      </p>
    </div>

    <!-- Sub-dialog que muestra el estatus del usuario -->
    <v-dialog :value="userStatusDialog" width="600" @input="$emit('status-dialog-input', $event)">
      <v-card>
        <v-card-text>
          <v-row justify="center">
            <v-col cols="10" md="7" class="mt-8 d-flex justify-center">
              <v-img
                src="@/assets/images/weChamber/logo-WC-main-nav-drawer.svg"
                max-width="220"
                height="auto"
                contain
              ></v-img>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="mt-2">
              <p
                v-if="userStatus == 'TO_VALIDATE'"
                class="text-center font-weight-bold headline mb-0"
              >
                Validación de correo
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <p v-if="userStatus == 'TO_VALIDATE'" class="title text-center">
                El correo del usuario aún no está validado, puede validarlo
                enviando un correo de validación con el botón de "Validar
                correo".
              </p>
              <p v-if="userStatus == 'SUSPENDED'" class="title text-center">
                El usuario ha sido suspendido, no puedes iniciar sesión.
              </p>
              <p v-if="userStatus == 'DELETED'" class="title text-center">
                El usuario ha sido eliminado, no puedes iniciar sesión.
              </p>
            </v-col>
          </v-row>
          <v-row
            class="d-flex pb-2 mt-5"
            :class="$vuetify.breakpoint.smAndUp ? 'justify-end' : 'justify-center'"
          >
            <v-btn
              class="font-weight-bold black--text mx-2"
              color="grey lighten-3"
              rounded
              elevation="0"
              @click="_closeUserStatusDialog"
            >
              Cerrar
            </v-btn>
            <v-btn
              v-if="userStatus == 'TO_VALIDATE'"
              class="font-weight-bold black--text mx-2"
              color="accent"
              rounded
              elevation="0"
              @click="$_resendEmailValidation"
            >
              Validar correo
            </v-btn>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapState } from "vuex";
import { validations } from "@/share/validations.js";
import { colors } from "@/constants/colors.js";
import { isAmpiMember } from "@/common/memberDashboardRouting";
import Endpoints from "@/share/Endpoints";
import defaultProfilePicture from "@/assets/images/weChamber/logo-WC-mark.svg";

export default {
  name: "LoginForm",
  props: {
    mode: {
      type: String,
      default: "page",
      validator: (v) => ["page", "dialog"].includes(v),
    },
    eventId: {
      type: String,
      default: null,
    },
    redirect: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loadingButton: false,
      validSignInForm: false,
      showPass: false,
      rememberMe: false,
      dataUser: {
        email: "",
        password: "",
      },
      emailError: "",
      emailLookupError: "",
      emailLookupValid: null,
      emailLookupTimer: null,
      emailLookupRequest: 0,
      associationProfilePicture: "",
      defaultProfilePicture,
      passwordError: "",
      userStatus: "",
      userStatusDialog: false,
      emailRules: validations.emailValidation({ required: true }),
      passwordRules: validations.generalFValidation({
        required: true,
        name: "contraseña",
      }),
      color: colors,
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    isLoginDisabled() {
      return (
        !this.dataUser.email ||
        !this.dataUser.password ||
        this.emailLookupValid !== true ||
        this.loadingButton
      );
    },
  },
  watch: {
    visible(newVal) {
      if (newVal === false) {
        this._resetForm();
      }
    },
  },
  methods: {
    ...mapActions("authentication", ["resendEmailValidation"]),
    ...mapActions("user", ["login"]),
    ...mapMutations("notification", ["show"]),
    ...mapMutations("user", ["setToken"]),
    ...mapMutations("loader", ["loading", "loaded"]),
    _resetForm() {
      this.dataUser = { email: "", password: "" };
      this.emailError = "";
      this.emailLookupError = "";
      this.emailLookupValid = null;
      this.passwordError = "";
      this.associationProfilePicture = "";
      this.loadingButton = false;
      this.showPass = false;
      this.userStatus = "";
      this.userStatusDialog = false;
      clearTimeout(this.emailLookupTimer);
    },
    _closeUserStatusDialog() {
      this.userStatusDialog = false;
      if (this.mode === "dialog") {
        this.$emit("close");
      }
    },
    validateField(field) {
      const rules = field === "email" ? this.emailRules : this.passwordRules;
      const value = this.dataUser[field];
      const error = rules.map((rule) => rule(value)).find((result) => result !== true);

      this[`${field}Error`] = error || "";
      this.validSignInForm = !this.emailError && !this.passwordError;
      return !error;
    },
    validateSignInForm() {
      const emailValid = this.validateField("email");
      const passwordValid = this.validateField("password");
      return emailValid && passwordValid;
    },
    handleEmailInput() {
      this.emailError = "";
      this.emailLookupError = "";
      this.emailLookupValid = null;
      this.associationProfilePicture = "";
      clearTimeout(this.emailLookupTimer);

      const email = this.dataUser.email;
      const emailValid = this.emailRules.every((rule) => rule(email) === true);
      if (!emailValid) {
        return;
      }

      this.emailLookupTimer = setTimeout(() => {
        this.validateEmailAvailability(email);
      }, 350);
    },
    async validateEmailAvailability(email = this.dataUser.email) {
      const requestId = ++this.emailLookupRequest;
      this.emailLookupValid = null;
      this.emailLookupError = "";

      console.info("[login-association][frontend-request]", {
        requestId,
        email: email.replace(/^(.{2}).*(@.*)$/, "$1***$2"),
      });

      try {
        const { data } = await this.axios.get(
          `${Endpoints.validateLoginEmail}?email=${encodeURIComponent(email)}`,
        );

        if (requestId !== this.emailLookupRequest || email !== this.dataUser.email) {
          return false;
        }

        this.emailLookupValid = data.valid === true;
        this.associationProfilePicture = data.associationProfilePicture || "";
        console.info("[login-association][frontend-response]", {
          requestId,
          valid: this.emailLookupValid,
          hasAssociationProfilePicture: Boolean(this.associationProfilePicture),
          associationProfilePicture: this.associationProfilePicture || null,
        });
        if (!this.emailLookupValid) {
          this.emailLookupError = "Este nombre de usuario no es válido";
        }
        return this.emailLookupValid;
      } catch (error) {
        console.error("[login-association][frontend-error]", {
          requestId,
          status: error?.response?.status || null,
          message: error?.message || "unknown error",
        });
        if (requestId === this.emailLookupRequest) {
          this.emailLookupValid = false;
          this.associationProfilePicture = "";
          this.emailLookupError = "No se pudo validar el nombre de usuario";
        }
        return false;
      }
    },
    handleAssociationImageError() {
      console.error("[login-association][image-error]", {
        associationProfilePicture: this.associationProfilePicture || null,
      });
      this.associationProfilePicture = "";
    },
    async _login() {
      if (!this.validateSignInForm()) {
        return;
      }

      if (this.emailLookupValid !== true) {
        const emailIsValid = await this.validateEmailAvailability();
        if (!emailIsValid) return;
      }

      this.loadingButton = true;

      try {
        const response = await this.login(this.dataUser);
        if (!response.status) {
          this.show({
            text: response.message,
            color: "error",
            time: 8000,
          });
          return;
        }

        if (this.mode === "dialog") {
          this.$emit("logged-in", response);
          this.loadingButton = false;
          return;
        }

        this.$gtag.set({ "Tipo de Usuario": response.role });

        const eventRedirect = this.eventId || null;
        const requestedRedirect = this.redirect;
        const memberRedirect =
          typeof requestedRedirect === "string" &&
          requestedRedirect.startsWith("/member-v2/")
            ? requestedRedirect
            : null;
        const isAmpiUser = isAmpiMember(this.user);
        const isMembershipUser = response.role === "MEMBERSHIP_USER";
        const isNotAmpiMember = response.role === "MEMBER" && !isAmpiUser;

        if (isAmpiUser) {
          if (eventRedirect) {
            this.$router.push(`/member/eventMicrosite/${eventRedirect}`);
          } else if (response.userState === "PROSPECTO") {
            this.$router.push("/member/home");
          } else if (response.userState === "ACTIVE" && memberRedirect) {
            this.$router.push(memberRedirect);
          } else {
            this.$router.push("/member/affiliation-profile");
          }
          return;
        }

        if (isMembershipUser || isNotAmpiMember) {
          if (eventRedirect) {
            this.$router.push(`/member/eventMicrosite/${eventRedirect}`);
          } else if (response.userState === "ACTIVE" && memberRedirect) {
            this.$router.push(memberRedirect);
          } else {
            this.$router.push("/member/home");
          }
          return;
        }

        if (response.userState === "VALIDATED") {
          this.$router.push("/panel-inicio");
        } else {
          this.userStatus = response.userState;
          this.userStatusDialog = true;
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingButton = false;
      }
    },
    async $_resendEmailValidation() {
      const response = await this.resendEmailValidation(this.dataUser.email);
      this.userStatusDialog = false;
      if (this.mode === "dialog") {
        this.$emit("close");
      }
      console.log("Esta es la respuesta");
      console.log(response);
    },
    passwordRecover() {
      this.$router.push("/recuperar-contrasena");
    },
  },
  beforeDestroy() {
    clearTimeout(this.emailLookupTimer);
    this.emailLookupRequest += 1;
  },
};
</script>

<style scoped>
.loginform-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at 63% 80%, rgba(224, 237, 255, 0.35), transparent 25%),
    linear-gradient(110deg, #f7faff 0%, #ffffff 52%, #f9fafc 100%);
  color: #1b273e;
}

.loginform-dialog {
  background: #ffffff;
  color: #1b273e;
}

.loginform-page__decor,
.loginform-page__dots {
  pointer-events: none;
  position: absolute;
}

.loginform-page__decor--orb-one {
  width: 840px;
  height: 840px;
  top: -475px;
  left: -445px;
  border-radius: 50%;
  background: radial-gradient(circle at 65% 68%, rgba(255, 255, 255, 0.94) 0 49%, rgba(231, 242, 255, 0.64) 50% 72%, rgba(214, 233, 255, 0.22) 73% 100%);
}

.loginform-page__decor--orb-two {
  width: 560px;
  height: 560px;
  right: -310px;
  bottom: -310px;
  border-radius: 50%;
  border: 1px solid rgba(220, 234, 255, 0.65);
  box-shadow: 0 0 0 48px rgba(233, 242, 255, 0.3), 0 0 0 112px rgba(241, 246, 255, 0.38);
}

.loginform-page__dots {
  width: 155px;
  height: 132px;
  opacity: 0.55;
  background-image: radial-gradient(#c4e2ff 1.2px, transparent 1.2px);
  background-size: 15px 15px;
}

.loginform-page__dots--top {
  top: 12px;
  left: 48px;
}

.loginform-page__dots--bottom {
  bottom: 74px;
  left: 160px;
  opacity: 0.34;
}

.loginform-back-button {
  position: absolute;
  top: 32px;
  left: 48px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid rgba(27, 39, 62, 0.12);
  border-radius: 999px;
  color: #1b273e !important;
  background: rgba(255, 255, 255, 0.72);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 5px 18px rgba(27, 39, 62, 0.06);
  transition: background 0.2s ease, transform 0.2s ease;
}

.loginform-back-button:hover {
  background: #ffffff;
  transform: translateY(-1px);
}

.loginform-page__layout {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: min(604px, calc(100% - 64px));
  min-height: 100vh;
  margin: 0 auto;
  padding: 154px 0 38px;
}

.loginform-dialog__layout {
  display: flex;
  align-items: stretch;
  flex-direction: column;
  width: 100%;
}

.loginform-page__card {
  width: 604px;
  min-height: 688px;
  padding: 47px 58px 43px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 11px 30px rgba(27, 39, 62, 0.09);
}

.loginform-dialog__card {
  width: 100%;
  padding: 32px 32px 28px;
  border-radius: 12px;
  background: #ffffff;
}

.loginform-card__header {
  display: flex;
  align-items: center;
  flex-direction: column;
}

.loginform-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 104px;
  height: 104px;
  overflow: hidden;
  border-radius: 50%;
  background: #ffffff;
}

.loginform-mark img {
  display: block;
  width: 64px;
  max-width: 64px;
  height: 64px;
  margin: 0;
  border-radius: 50%;
  overflow: hidden;
  clip-path: circle(50%);
  object-fit: contain;
  object-position: center;
  position: relative;
  top: auto;
}

.loginform-mark--default {
  border-radius: 0;
}

.loginform-mark--default img {
  border-radius: 0;
  clip-path: none;
}

.loginform-mark--association img {
  width: 100%;
  max-width: 100%;
  height: 100%;
  margin: 0;
  object-fit: cover;
  border-radius: 50%;
  transform: scale(1.18);
  transform-origin: center;
}

.loginform-mark-flip-enter-active,
.loginform-mark-flip-leave-active {
  transition: transform 0.5s ease;
  backface-visibility: hidden;
  border-radius: 50% !important;
  overflow: hidden;
  clip-path: circle(50%) !important;
}

.loginform-mark-flip-enter,
.loginform-mark-flip-leave-to {
  transform: rotateY(90deg) !important;
}

.loginform-card__header h2 {
  margin: 25px 0 3px;
  color: #202020;
  font-size: 31px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.6px;
}

.loginform-card__header p {
  margin: 0;
  color: #5f6570;
  font-size: 16px;
  font-weight: 500;
}

.loginform-dialog .loginform-card__header h2 {
  margin-top: 18px;
  font-size: 26px;
}

.loginform-dialog .loginform-card__header p {
  font-size: 14px;
}

.loginform-dialog .loginform-mark {
  width: 80px;
  height: 80px;
}

.loginform-dialog .loginform-mark img {
  width: 50px;
  height: 50px;
  max-width: 50px;
}

.loginform-dialog .loginform-mark--association img {
  width: 100%;
  height: 100%;
  max-width: 100%;
}

.loginform-form {
  margin-top: 27px;
}

.loginform-dialog .loginform-form {
  margin-top: 18px;
}

.loginform-field + .loginform-field {
  margin-top: 21px;
}

.loginform-field label {
  display: block;
  margin-bottom: 8px;
  color: #4d5565;
  font-size: 14px;
  font-weight: 500;
}

.loginform-field__control {
  display: flex;
  align-items: center;
  height: 55px;
  padding: 0 16px;
  border: 1px solid #e4e6ea;
  border-radius: 9px;
  background: #ffffff;
  box-shadow: 0 2px 7px rgba(27, 39, 62, 0.08);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.loginform-field__control:focus-within {
  border-color: #24b2da;
  box-shadow: 0 0 0 3px rgba(36, 178, 218, 0.13);
}

.loginform-field__control--error {
  border-color: #ba1a1a;
}

.loginform-field__control > .v-icon {
  flex: 0 0 auto;
  color: #9ba4b2;
  font-size: 22px;
}

.loginform-field__control input {
  min-width: 0;
  flex: 1;
  height: 100%;
  margin-left: 16px;
  border: 0;
  outline: 0;
  color: #1b273e;
  background: transparent;
  font-size: 15px;
}

.loginform-field__control input::placeholder {
  color: #aeb5c1;
  opacity: 1;
}

.loginform-field__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  height: 32px;
  margin-left: 8px;
  color: #9ba4b2;
}

.loginform-field__toggle:hover {
  color: #1b273e;
}

.loginform-field__toggle .v-icon {
  font-size: 20px;
}

.loginform-field__error {
  display: block;
  margin-top: 5px;
  color: #ba1a1a;
  font-size: 12px;
}

.loginform-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 19px;
}

.loginform-remember {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #626a79;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
}

.loginform-remember input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.loginform-remember__box {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 1px solid #d2d7df;
  border-radius: 4px;
  background: #ffffff;
}

.loginform-remember input:checked + .loginform-remember__box {
  border-color: #24b2da;
  background: #24b2da;
  box-shadow: inset 0 0 0 3px #ffffff;
}

.loginform-forgot {
  color: #147fe5;
  font-size: 14px;
  font-weight: 600;
  text-align: right;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.loginform-forgot:hover {
  text-decoration: underline;
}

.loginform-submit,
.loginform-google {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
}

.loginform-submit {
  position: relative;
  justify-content: center;
  height: 47px;
  margin-top: 36px;
  padding: 0 22px;
  color: #101010;
  background: linear-gradient(90deg, #ffb500 0%, #ffb313 100%);
  box-shadow: 0 3px 5px rgba(255, 181, 0, 0.18);
  transition: filter 0.2s ease, transform 0.2s ease;
}

.loginform-dialog .loginform-submit {
  margin-top: 24px;
}

.loginform-submit span {
  white-space: nowrap;
}

.loginform-submit .v-icon {
  position: absolute;
  right: 22px;
  color: #101010;
  font-size: 23px;
}

.loginform-submit:not(:disabled):hover {
  filter: brightness(1.03);
  transform: translateY(-1px);
}

.loginform-submit:disabled {
  cursor: not-allowed;
  opacity: 1;
}

.loginform-divider {
  display: flex;
  align-items: center;
  gap: 26px;
  margin: 33px 0 28px;
  color: #596273;
}

.loginform-dialog .loginform-divider {
  margin: 22px 0 18px;
  gap: 14px;
}

.loginform-divider span {
  height: 1px;
  flex: 1;
  background: #e2e5ea;
}

.loginform-divider small {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.loginform-google-wrapper {
  display: block;
  width: 100%;
}

.loginform-google {
  height: 48px;
  border: 1px solid #e9ebef;
  color: #4c5360;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(27, 39, 62, 0.07);
}

.loginform-google:disabled {
  cursor: not-allowed;
  opacity: 0.78;
}

.loginform-google__mark {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  margin-right: 17px;
  object-fit: contain;
}

.loginform-terms {
  width: 100%;
  margin: 32px 0 0;
  color: #687283;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}

.loginform-terms a {
  margin: 0 4px;
  color: #147fe5;
  text-decoration: underline;
}

@media (max-width: 820px) {
  .loginform-page {
    overflow: auto;
  }

  .loginform-back-button {
    top: 18px;
    right: auto;
    left: 20px;
    padding: 8px 12px;
    font-size: 12px;
  }

  .loginform-page__layout {
    width: min(604px, calc(100% - 32px));
    min-height: auto;
    padding: 86px 0 32px;
  }

  .loginform-page__card {
    width: 100%;
    min-height: 0;
  }

  .loginform-terms {
    margin-top: 22px;
  }

  .loginform-page__dots--bottom {
    display: none;
  }
}

@media (max-width: 520px) {
  .loginform-page__layout {
    width: calc(100% - 24px);
    padding-top: 76px;
  }

  .loginform-back-button {
    top: 18px;
    left: 20px;
    right: auto;
    padding: 8px 12px;
  }

  .loginform-page__card {
    padding: 36px 22px 30px;
    border-radius: 14px;
  }

  .loginform-card__header h2 {
    font-size: 28px;
  }

  .loginform-options {
    align-items: flex-start;
    flex-direction: column;
  }

  .loginform-forgot {
    align-self: flex-end;
  }

  .loginform-submit {
    padding: 0 22px;
  }

  .loginform-divider {
    gap: 12px;
  }

  .loginform-terms {
    padding: 0 8px;
    font-size: 12px;
  }
}
</style>
